import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  InputAdornment,
  CircularProgress,
  Snackbar,
  Alert,
  Grid,
  Card,
  CardContent,
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import QuotationService from "../services/QuotationService";
import QuotationDetailDialog from "../components/QuotationDetailDialog";
import QuotationSendDialog from "../components/QuotationSendDialog";
import {
  formatMoney,
  formatQuotationLabel,
  QUOTATION_STATUSES,
} from "../utils/quotationUtils";
import { PAGE_BG } from "../components/leadConstants";

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function statusMeta(status) {
  return QUOTATION_STATUSES.find((s) => s.value === status) || { label: status, color: "default" };
}

export default function QuotationsView() {
  const navigate = useNavigate();
  const [quotations, setQuotations] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [sendTarget, setSendTarget] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [sending, setSending] = useState(false);
  const [converting, setConverting] = useState(false);
  const [invoicing, setInvoicing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [listData, summaryData] = await Promise.all([
        QuotationService.getQuotations({
          search: searchQuery || undefined,
          page: pagination.page,
          limit: 25,
        }),
        QuotationService.getStatusSummary(),
      ]);
      setQuotations(listData.quotations || []);
      setPagination({
        page: listData.page,
        totalPages: listData.totalPages,
        total: listData.total,
      });
      setSummary(summaryData);
    } catch {
      setSnackbar({ open: true, message: "Failed to load quotations", severity: "error" });
    } finally {
      setLoading(false);
    }
  }, [searchQuery, pagination.page]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openDetail = async (quotation) => {
    try {
      const full = await QuotationService.getQuotationById(quotation.id);
      setSelectedQuotation(full);
      setDetailOpen(true);
    } catch {
      setSnackbar({ open: true, message: "Failed to load quotation details", severity: "error" });
    }
  };

  const handleDelete = async (quotation) => {
    if (!window.confirm(`Delete ${formatQuotationLabel(quotation.quotation_number)}?`)) return;
    try {
      await QuotationService.deleteQuotation(quotation.id);
      setDetailOpen(false);
      setSelectedQuotation(null);
      setSnackbar({ open: true, message: "Quotation deleted", severity: "success" });
      loadData();
    } catch {
      setSnackbar({ open: true, message: "Failed to delete quotation", severity: "error" });
    }
  };

  const handleStatusChange = async (quotation, status) => {
    try {
      const updated = await QuotationService.updateStatus(quotation.id, status);
      setSelectedQuotation(updated);
      setSnackbar({ open: true, message: "Status updated", severity: "success" });
      loadData();
    } catch {
      setSnackbar({ open: true, message: "Failed to update status", severity: "error" });
    }
  };

  const handleDownloadPdf = async (quotation) => {
    setDownloading(true);
    try {
      const { blob, filename } = await QuotationService.downloadPdf(quotation.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setSnackbar({ open: true, message: "PDF downloaded", severity: "success" });
    } catch {
      setSnackbar({ open: true, message: "Failed to download PDF", severity: "error" });
    } finally {
      setDownloading(false);
    }
  };

  const handleOpenSendDialog = (quotation) => {
    setSendTarget(quotation);
    setSendDialogOpen(true);
  };

  const handleSendQuotation = async (payload) => {
    if (!sendTarget) return;
    setSending(true);
    try {
      const result = await QuotationService.sendQuotation(sendTarget.id, payload);
      setSelectedQuotation(result?.quotation || sendTarget);
      setSnackbar({ open: true, message: "Quotation sent via WhatsApp", severity: "success" });
      setSendDialogOpen(false);
      setSendTarget(null);
      loadData();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || err?.message || "Failed to send quotation",
        severity: "error",
      });
      throw err;
    } finally {
      setSending(false);
    }
  };

  const handleConvertToOrder = async (quotation, deliveryAddress, paymentMode = "cod") => {
    setConverting(true);
    try {
      const result = await QuotationService.convertToOrder(quotation.id, {
        deliveryAddress,
        paymentMode,
      });
      setSelectedQuotation(result?.quotation || quotation);
      let message;
      if (result?.delivery?.mode === "native_order_details") {
        message = "Quotation converted. WhatsApp payment request sent to customer.";
      } else if (result?.delivery?.mode === "cod_confirmation") {
        message = "Quotation converted. COD order created and confirmation sent on WhatsApp.";
      } else {
        message = "Quotation converted to order.";
      }
      const platform = result?.commerce_platform ? ` (${result.commerce_platform})` : "";
      setSnackbar({
        open: true,
        message: `${message}${platform}`,
        severity: "success",
      });
      loadData();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || err?.message || "Failed to convert quotation",
        severity: "error",
      });
      throw err;
    } finally {
      setConverting(false);
    }
  };

  const handleConvertToInvoice = async (quotation) => {
    if (!window.confirm(`Send proforma invoice for ${formatQuotationLabel(quotation.quotation_number)} to the customer via WhatsApp?`)) {
      return;
    }
    setInvoicing(true);
    try {
      const result = await QuotationService.convertToInvoice(quotation.id);
      setSelectedQuotation(result?.quotation || quotation);
      setSnackbar({
        open: true,
        message: "Proforma invoice sent to customer",
        severity: "success",
      });
      loadData();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || err?.message || "Failed to send invoice",
        severity: "error",
      });
    } finally {
      setInvoicing(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: PAGE_BG, minHeight: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, gap: 2, flexWrap: "wrap" }}>
        <Typography variant="h5">
          Quotations (
          {pagination.total}
          )
        </Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <TextField
            size="small"
            placeholder="Search quotations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 240 }}
          />
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadData}>Refresh</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate("/quotations/new")}>
            Create Quotation
          </Button>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {(summary.length ? summary : [{ status: "created", count: 0, total_minor: 0 }]).map((item) => {
          const meta = statusMeta(item.status);
          return (
            <Grid item xs={12} sm={6} md={3} key={item.status}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "primary.main" }} />
                    <Typography variant="body2">
                      {meta.label}
                      {" "}
                      -
                      {" "}
                      {item.count}
                    </Typography>
                  </Box>
                  <Typography variant="h6">{formatMoney(item.total_minor)}</Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Paper>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Quotation ID</TableCell>
                  <TableCell>Lead Name</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last opened</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {quotations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                        No quotations yet. Create your first quotation to get started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  quotations.map((quotation) => {
                    const meta = statusMeta(quotation.status);
                    const lastOpened = quotation.send_delivery_type === "url"
                      ? (quotation.last_url_seen_at
                        ? formatDateTime(quotation.last_url_seen_at)
                        : "Not opened")
                      : "";
                    return (
                      <TableRow key={quotation.id} hover>
                        <TableCell>{formatQuotationLabel(quotation.quotation_number)}</TableCell>
                        <TableCell>{quotation.lead_title || quotation.contact_name || "—"}</TableCell>
                        <TableCell>
                          <Typography variant="body2">{quotation.created_by_account_name || "—"}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDateTime(quotation.created_at)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={meta.label} color={meta.color} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color={lastOpened ? "text.primary" : "text.secondary"}>
                            {lastOpened || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{formatMoney(quotation.total_minor, quotation.currency)}</TableCell>
                        <TableCell align="right">
                          <IconButton size="small" color="primary" onClick={() => openDetail(quotation)}>
                            <ViewIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="success" onClick={() => navigate(`/quotations/${quotation.id}/edit`)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDelete(quotation)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Total
          {" "}
          {pagination.total}
          {" "}
          items
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            size="small"
            disabled={pagination.page <= 1}
            onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
          >
            Previous
          </Button>
          <Button size="small" variant="outlined">{pagination.page}</Button>
          <Button
            size="small"
            disabled={pagination.page >= pagination.totalPages}
            onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
          >
            Next
          </Button>
        </Box>
      </Box>

      <QuotationDetailDialog
        open={detailOpen}
        quotation={selectedQuotation}
        onClose={() => {
          setDetailOpen(false);
          setSelectedQuotation(null);
        }}
        onEdit={(quotation) => {
          setDetailOpen(false);
          navigate(`/quotations/${quotation.id}/edit`);
        }}
        onDelete={handleDelete}
        onStatusChange={handleStatusChange}
        onDownloadPdf={handleDownloadPdf}
        onSendWhatsApp={handleOpenSendDialog}
        onConvertToOrder={handleConvertToOrder}
        onConvertToInvoice={handleConvertToInvoice}
        downloading={downloading}
        sending={sending}
        converting={converting}
        invoicing={invoicing}
      />

      <QuotationSendDialog
        open={sendDialogOpen}
        quotation={sendTarget}
        onClose={() => {
          setSendDialogOpen(false);
          setSendTarget(null);
        }}
        onSend={handleSendQuotation}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
